import { RiskGroupShortCodeEnum } from '../enums/risk-group-short-code.enum';
import { WithdrawalSourcingMode } from '../enums/withdrawal-sourcing-mode.enum';
import { TaskResolutionEnum, RiskLevelEnum } from '../enums/task.enums';
import { PagedResultDto } from './paged-result.dto';
import { WithdrawalTypeEnum } from './wallet.model';

export interface Transaction {
  id: string;
  direction: string;
  transactionDirection?: string;
  sendingWallet: string;
  receivingWallet: string;
  cryptocurrency: string;
  amount: number;
  amountDisplayPrecision: number;
  amountBaseCurrency: number;
  baseCurrency?: string;
  baseCurrencySymbol: string;
  baseCurrencyDisplayPrecision?: number;
  totalFees: number;
  network?: string;
  date: Date;
  riskLevel: string;
  exchangeCategoryName?: string;
  riskCategoryName?: string;
  riskExposureName?: string;
  warningDetails?: WarningDetails;
  status: string;
  approvedBy?: string;
  notes?: string;
  comments?: string;
  settlementDate?: Date;
  preparedBy?: string;
  resolvedBy?: string;
  createdAt: Date;
  updatedAt?: Date;
  merchantId?: string;
  merchantName?: string;
  currencyId: string;
  networkFee?: number;
  transactionHash?: string;
  hasAwaitingApproval: boolean;
  resolution?: TaskResolutionEnum;
  resolvedByDisplayName?: string;
  resolvedAt?: string;
  tronNetworkInfo?: TronNetworkInfoDto;
  ethereumNetworkInfo?: EthereumNetworkInfoDto;
  riskAssessmentCompleted: boolean;
  walletName?: string;
  canCancelPrepared: boolean;
  canSpeedup: boolean;
  canCancel: boolean;
  canReverse: boolean;
  allowsAdjustment: boolean;
  reason?: string;
  externalComments?: string;
  isPaymentApi?: boolean;
  showExternalComments?: boolean;
  sendingWalletName: string;
  receivingWalletName: string;
  isRetried?: boolean;
  hangfireFailure?: boolean;
  hangfireFailureReason?: string;
  rate?: number;
  rateDisplayPrecision?: number;
  tradeType?: TradeTypeEnum;
  canRecordExchange?: boolean;
  fundingMerchantId?: string | null;
  isExchangeRateOverridden?: boolean;
  requiresExchangeRateOverride?: boolean;
  lastKnownExchangeRate?: number | null;
  maxExchangeRateDeviationPercent?: number | null;
  exchangeRate?: number | null;
  exchangeRatePrecision?: number | null;
  linkedExchangeRateTransactions?: LinkedExchangeRateTransaction[] | null;
}

export interface LinkedExchangeRateTransaction {
  transactionId: string;
  transactionType: string;
}

export interface WarningDetails {
  title: string;
  description: string;
}

export interface TronNetworkInfoDto {
  trxBurned?: number;
  trxBurnedDisplayPrecision?: number;
  trxBurnedBaseCurrency?: number;
  trxBurnedBaseCurrencyDisplayPrecision?: number;
  energyUsed?: number;
  bandwidthUsed?: number;
}

export interface EthereumNetworkInfoDto {
  gasFeeUsedEth?: number;
  gasFeeUsedEthDisplayPrecision?: number;
  gasFeeUsedBaseCurrency?: number;
  gasFeeUsedBaseCurrencyDisplayPrecision?: number;
}

export interface TransactionFilter {
  actionRequired?: boolean[];
  transactionType?: number[];
  tradeType?: number[];
  status?: number[];
  blockchain?: number[];
  riskLevel?: number[];
  exchangeCategories?: number[];
  account?: string;
  filter?: string;
  sorting?: string;
  skipCount?: number;
  maxResultCount?: number;
  startDate?: string;
  endDate?: string;
  merchantIds?: string[];
  exchangeRateOverridden?: boolean;
}
export interface DashboardTransaction extends Transaction {
  actionRequired?: boolean;
  merchantName?: string;
  settlementBatch?: string;
  reason?: string;
  confirmations?: string;
  feeType?: string;
  relatedTransaction?: string;
  errorCode?: string;
  errorMessage?: string;
  estimatedCompletion?: string;
}

export enum TransactionTypeEnum {
  Fee = 0,
  Deposit = 1,
  Withdrawal = 2,
  Transfer = 3,
  CreditAdjustment = 4,
  DebitAdjustment = 5,
  Exchange = 6,
  DepositReversal = 18,
  PreLaunchDeposit = 19,
  BurnRefund = 20,
}

export enum TradeTypeEnum {
  Buy = 1,
  Sell = 2,
}

export enum TransactionStatusEnum {
  Pending = 0,
  Rejected = 1,
  Confirmed = 2,
  Failed = 3,
}

export enum TransactionAdjustmentReasonEnum {
  AccountOverspent = 0,
  MerchantGoodwill = 1,
}

export interface ApproveTransactionRequestDto {
  totp: string;
  comments?: string;
}

export interface SetTransactionRiskRequestDto {
  totp: string;
  riskLevel: RiskLevelEnum;
  comments?: string;
}
export interface RejectTransactionRequestDto {
  comments?: string;
}

export interface CancelTransactionDto {
  totp: string;
}

export interface RetryTransactionDto {
  isRetried: boolean;
  totp: string;
}

export interface ReverseTransactionDto {
  destinationWalletAddress: string;
  totp: string;
}

export interface PrepareTransactionRequestDto {
  senderWalletId?: string;
  receiverAddress?: string;
  receiverWalletId?: string;
  amount: number;
  currencyId: string;
  merchantId?: string;
  withdrawalType: WithdrawalTypeEnum;
  memo?: string;
  totp: string;
  mode?: WithdrawalSourcingMode;
  targetRiskGroup?: RiskGroupShortCodeEnum;
}

export interface PrepareSystemTransactionRequestDto {
  senderWalletId?: string;
  receiverAddress?: string;
  receiverWalletId?: string;
  amount: number;
  currencyId: string;
  withdrawalType: WithdrawalTypeEnum;
  memo?: string;
  totp: string;
}

export interface AdjustTransactionRequestDto {
  transactionId: string;
  adjustmentType: number;
  amount: number;
  reason: number;
  totp: string;
}

export type PagedMerchantTransactionResult = PagedResultDto<Transaction>;

export interface PrepareDepositRequest {
  merchantEntityId: string;
  blockchain: number;
  walletType: number;
  currencyId: string;
  amount?: number;
}

export interface PrepareDepositResponse {
  identifier: string;
  walletAddress: string;
}

export interface AcknowledgeIncompleteRiskDto {
  comment?: string;
}

